// Supabase Storage Service — v242.90.0
// SECURITY: No Supabase key ships in the client. Large patient files upload
// directly to a PRIVATE bucket via a one-time SIGNED UPLOAD (a presigned POST with
// the 25 MB cap since PROD-258, v242.239.0) minted by the Orella API (which holds
// the secret key server-side). uploadToStorage returns
// the storage PATH; the API resolves paths to signed download URLs on read.
// PROD-849: the path is returned only after POST /files/confirm (confirmUpload below).

import { authGet } from '../utils/authStorage'

const API_BASE = import.meta.env.VITE_API_URL ||
  (import.meta.env.PROD ? 'https://api.orellahealth.com/api/v1' : 'http://localhost:8000/api/v1')
const TOKEN_KEY = 'orella_patient_token'

// ★★★ v242.90.0 (TF b37 #5) — READ THE TOKEN THROUGH authStorage, NEVER DIRECTLY.
//   v242.87.0 moved auth to `utils/authStorage`, which on NATIVE writes to
//   localStorage (so the token survives a WKWebView content-process recycle —
//   that was the fix for TF b34 #13/#17). Every consumer was migrated except
//   this file, which kept reading `sessionStorage.getItem(TOKEN_KEY)`.
//   On iOS that read returns null 100% of the time, so uploadToStorage bailed
//   before making a single request and EVERY upload in the app was dead:
//   Detect photos/videos/documents, the external doctor's note, lab results,
//   follow-up reports, authorisations — and Describe voice notes, which fail
//   with no user-visible message at all. On web it kept working, because there
//   authStorage and this line both resolve to sessionStorage. That is why it
//   shipped. `scripts/check-auth-storage.mjs` is the gate.

/**
 * Convert a base64 data URL to a Blob
 */
function dataURLtoBlob(dataUrl) {
  const parts = dataUrl.split(',')
  const mime = parts[0].match(/:(.*?);/)?.[1] || 'application/octet-stream'
  const byteString = atob(parts[1])
  const ab = new ArrayBuffer(byteString.length)
  const ia = new Uint8Array(ab)
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i)
  }
  return new Blob([ab], { type: mime })
}

/**
 * Upload a file, returning WHY it failed.
 *
 * ★★ v242.90.0 — the old function collapsed six distinct failures into a bare
 *   `null`, and DetectScreen then rendered `t('detect.uploadError', { message:
 *   file.name })` — a template whose parameter is literally named `message` and
 *   is handed a FILENAME. So a 401, a 413, a dead network and a missing token
 *   all showed the patient the same four words, and told triage nothing. The
 *   status code was read at the `!signRes.ok` branch *specifically in order to
 *   log it*, then thrown away. This core returns it instead.
 *
 * @returns {{ok: true, path: string} | {ok: false, reason: string, status?: number, detail?: string, code?: string}}
 *   reason ∈ invalid-data | no-auth | sign-failed | malformed-sign | upload-failed | exception
 *   (PROD-849: a confirm verdict is upload-failed with its status and `code`; see confirmUpload)
 */
export async function uploadToStorageResult(caseId, fileName, fileData, mimeType) {
  try {
    // Normalize input to a Blob
    let blob
    if (typeof fileData === 'string' && fileData.startsWith('data:')) {
      blob = dataURLtoBlob(fileData)
    } else if (fileData instanceof Blob || fileData instanceof File) {
      blob = fileData
    } else {
      console.error('[Storage] Invalid file data type:', typeof fileData)
      return { ok: false, reason: 'invalid-data' }
    }

    // ★ authGet, NOT sessionStorage — see the note at the top of this file.
    const token = authGet(TOKEN_KEY)
    if (!token) {
      console.error('[Storage] No auth token — cannot request upload URL')
      return { ok: false, reason: 'no-auth' }
    }

    const contentType = mimeType || blob.type || 'application/octet-stream'

    // 1) Ask the API for a one-time signed upload. ★★ PROD-258 (patient P2): a presigned POST
    //    (`upload: 'post'` + the real `size`), which carries the 25 MB cap and the type rule
    //    (orella-api N4 step 4, live since v243.271.0). The presigned PUT this asked for before had
    //    no size cap: the 25 MB limit of /files/upload was bypassable through it. An API without
    //    the flag (or the supabase backend) still answers today's PUT URL; sendToPresigned takes
    //    both. A mint refusal (413 too large, 415 type, 400 no size) comes back as sign-failed with
    //    its status, as every other mint refusal does.
    const signRes = await fetch(`${API_BASE}/cases/${caseId}/upload-url`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ fileName, mimeType: contentType, upload: 'post', size: blob.size }),
    })
    if (!signRes.ok) {
      const errTxt = await signRes.text().catch(() => '')
      console.error(`[Storage] Could not get upload URL (${signRes.status}), response length ${errTxt.length}`)
      return { ok: false, reason: 'sign-failed', status: signRes.status, detail: errTxt.slice(0, 200) }
    }
    const signed = await signRes.json()
    const path = signed?.path
    if (!path || (!signed.upload && !signed.uploadUrl)) {
      console.error('[Storage] Malformed sign response')
      return { ok: false, reason: 'malformed-sign' }
    }

    console.log(`[Storage] Uploading (${(blob.size / 1024 / 1024).toFixed(2)} MB)`)

    // 2) Upload the bytes straight to storage with what the mint answered (no key ships).
    const up = await sendToPresigned(signed, blob, { 'Content-Type': contentType, 'x-upsert': 'true' })
    if (!up.ok) {
      console.error(`[Storage] Upload failed (${up.status})`)
      return { ok: false, reason: 'upload-failed', status: up.status, detail: (up.detail || '').slice(0, 200) }
    }

    // 3) PROD-849 (PROD-745 follow-up): the path is used only after POST /files/confirm. One of
    //    the three verdicts comes back as upload-failed with its status, exactly like a failed
    //    transfer, so every caller handles it the way it already handles a failed upload.
    const conf = await confirmUpload(path, caseId, token)
    if (!conf.ok) return { ok: false, reason: 'upload-failed', status: conf.status, code: conf.code }

    // 4) Return the storage PATH. The API turns this into a signed URL on read.
    console.log('[Storage] \u2705 Upload successful')
    return { ok: true, path }
  } catch (err) {
    console.error('[Storage] Upload error:', err)
    return { ok: false, reason: 'exception', detail: String(err?.message || err).slice(0, 200) }
  }
}

/**
 * PROD-258 (patient P2) — send a blob to what the upload mint answered. The same rule as the MD
 * panel's sendToPresigned (orella-md-panel src/services/storage.js, N4 step 9).
 *
 * ★★ `upload` present (s3 backend): a presigned POST. The returned fields go into the form IN
 *   THEIR RETURNED ORDER and the file goes LAST (S3 ignores every field after `file`), with NO
 *   headers: the browser sets the multipart boundary, and the policy pins Content-Type as a form
 *   FIELD. No x-upsert (not a POST field; the policy would refuse it).
 * ★★ A FAILED POST IS NEVER RETRIED AS A PUT. The PUT URL is the uncapped path the POST exists to
 *   replace; falling back to it on a 400 EntityTooLarge would store what the cap refused.
 * ★ `upload` absent (supabase backend, or an API without the flag): today's PUT, with a warning.
 * @returns {Promise<{ok: boolean, status: number, method: 'post'|'put'|'none', detail?: string}>}
 */
export async function sendToPresigned(signed, blob, putHeaders) {
  if (signed && signed.upload) {
    const up = signed.upload
    if (up.method !== 'POST' || typeof up.url !== 'string' || !up.url || !up.fields || typeof up.fields !== 'object') {
      return { ok: false, status: 0, method: 'post', detail: 'malformed POST policy' }
    }
    const form = new FormData()
    for (const [k, v] of Object.entries(up.fields)) form.append(k, v)
    form.append('file', blob)
    const res = await fetch(up.url, { method: 'POST', body: form })
    const detail = res.ok ? '' : await res.text().catch(() => '')
    return { ok: res.ok, status: res.status, method: 'post', detail }
  }
  if (!signed || typeof signed.uploadUrl !== 'string' || !signed.uploadUrl) return { ok: false, status: 0, method: 'none' }
  console.warn('[Storage] the mint answered a PUT URL (no POST policy): uploading by PUT')
  const res = await fetch(signed.uploadUrl, { method: 'PUT', headers: putHeaders, body: blob })
  const detail = res.ok ? '' : await res.text().catch(() => '')
  return { ok: res.ok, status: res.status, method: 'put', detail }
}

/** PROD-849: the only confirm answers that refuse an upload (code AND status must match; see confirmUpload). */
export const UPLOAD_CONFIRM_REFUSALS = Object.freeze({
  upload_key_reused: 409,
  upload_not_found: 404,
  upload_check_unavailable: 503,
})

/** PROD-849 r_849 S1: a confirm still unanswered after this many ms is aborted and the upload is kept. */
export const UPLOAD_CONFIRM_TIMEOUT_MS = 10000

/**
 * PROD-849 (PROD-745 follow-up) — confirm a direct upload before its path is used. The same rule
 * as the MD panel's confirmUpload (orella-md-panel src/services/storage.js, PROD-849 md half).
 *
 * POST /api/v1/files/confirm { path, caseId }. The API checks write access to the key and, with
 * UPLOAD_VERSION_CHECK_ENABLED on the s3 backend, counts the versions of exactly that key on the
 * PHI bucket (a presigned POST stays valid for 15 minutes and can be re-sent to the same key).
 *
 * ★★ FAIL-OPEN EXCEPT FOR THE THREE VERDICTS. Only these answers refuse the upload (code AND
 *   status must match):
 *     409 upload_key_reused         the key holds more than one version (the POST was re-sent)
 *     404 upload_not_found          the key holds no version
 *     503 upload_check_unavailable  the API could not list the versions (the API is fail-closed)
 *   The API answers each of them ONLY with the flag ON. With the flag OFF (the default until
 *   PROD-746) it answers 200 versionCheck 'off' after the access check, so uploads behave as
 *   before. Every other answer keeps the upload: a 403 decided nothing about the object (the
 *   confirm write check is stricter than the mint: PROD-745 review N2), a 404 without a code is an
 *   API without the route, and a network error or 5xx decided nothing either.
 * ★★ A CONFIRM THAT DOES NOT ANSWER IS A NETWORK ERROR (r_849 S1). The confirm runs after the
 *   upload has already landed, so it must never hold the upload (the Detect spinner, a Deliver
 *   follow-up photo): after UPLOAD_CONFIRM_TIMEOUT_MS the request is aborted and the upload is
 *   kept. The deadline covers the answer AND its body (both are raced against it), and the timer
 *   is cleared on every exit.
 * ★ Logs carry the status and the code only, never the key or the case id.
 * @returns {Promise<{ok: true} | {ok: false, status: number, code: string}>}
 */
export async function confirmUpload(path, caseId, token) {
  const abort = new AbortController()
  let timer = null
  let timedOut = false
  const deadline = new Promise((resolve) => {
    timer = setTimeout(() => { timedOut = true; abort.abort(); resolve(null) }, UPLOAD_CONFIRM_TIMEOUT_MS)
  })
  try {
    let res
    try {
      res = await Promise.race([fetch(`${API_BASE}/files/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ path, caseId }),
        signal: abort.signal,
      }), deadline])
    } catch {
      res = null
    }
    if (timedOut) {
      console.warn('[Storage] Upload confirm timeout; the upload is kept')
      return { ok: true }
    }
    if (!res) {
      console.warn('[Storage] Upload confirm did not answer; the upload is kept')
      return { ok: true }
    }
    if (res.ok) return { ok: true }
    let code = null
    try {
      const body = await Promise.race([res.json(), deadline])
      code = body && typeof body.code === 'string' ? body.code : null
    } catch {
      code = null
    }
    if (timedOut) {
      console.warn('[Storage] Upload confirm timeout; the upload is kept')
      return { ok: true }
    }
    const status = res.status
    if (code && Object.prototype.hasOwnProperty.call(UPLOAD_CONFIRM_REFUSALS, code) && UPLOAD_CONFIRM_REFUSALS[code] === status) {
      console.error(`[Storage] Upload refused at confirm (${status} ${code})`)
      return { ok: false, status, code }
    }
    console.warn(`[Storage] Upload confirm answered ${status}; the upload is kept`)
    return { ok: true }
  } finally {
    clearTimeout(timer)
  }
}

/**
 * Path-or-null wrapper. Eight call sites still use this shape; only the one
 * with a visible error banner (DetectScreen) reads the result form above.
 * @returns {string|null} Storage PATH of the uploaded file, or null on failure
 */
export async function uploadToStorage(caseId, fileName, fileData, mimeType) {
  const res = await uploadToStorageResult(caseId, fileName, fileData, mimeType)
  return res.ok ? res.path : null
}

